﻿import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { MessageSquare, X, PartyPopper, Flame, RefreshCw, Sparkles, AlertTriangle, ChevronLeft, ChevronRight, Clock, Globe, Shield, Info } from 'lucide-react';
import { PointIcon } from './icons/PointIcon';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

function fmtDay(d: Date) { return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }); }
function fmtHour(d: Date) { return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }



function isMealPoint(pt: any) {
  if (!pt) return false;
  return !!pt.isMealPoint || /déjeuner|dejeuner|diner|dîner/i.test(pt.name);
}

const STAGE_COLORS = [
  { border: '#A78BFA', bg: '#8B5CF6aa' }, // Violet
  { border: '#F472B6', bg: '#EC4899aa' }, // Pink/Rose
  { border: '#2DD4BF', bg: '#14B8A6aa' }, // Teal
  { border: '#22D3EE', bg: '#06B6D4aa' }, // Cyan
  { border: '#FB923C', bg: '#F97316aa' }, // Orange
  { border: '#60A5FA', bg: '#3B82F6aa' }, // Blue
  { border: '#FBBF24', bg: '#F59E0Baa' }, // Amber/Yellow
  { border: '#34D399', bg: '#10B981aa' }, // Emerald
  { border: '#C084FC', bg: '#A855F7aa' }, // Purple
  { border: '#FB7185', bg: '#F43F5Eaa' }, // Rose/Red
];

export function GanttTimeline({ onNavigate }: { onNavigate?: () => void }) {
  const { currentUser, team: allTeam, shifts, points, allPoints, categories, addMessage, updateShift, deleteShift, addShift, currentOrgId, impersonatedTeamId } = useVisibleStore();
  const team = useMemo(() => allTeam.filter(s => !s.parentArtistId), [allTeam]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const now = new Date();

  // Generate distinct colors for each stage point automatically
  const stageColorsMap = useMemo(() => {
    const map: Record<string, { border: string; bg: string }> = {};
    const stagePoints = (allPoints || points).filter((p: any) => p.isStage).sort((a: any, b: any) => a.name.localeCompare(b.name));
    stagePoints.forEach((p: any, idx: number) => {
      map[p.id] = STAGE_COLORS[idx % STAGE_COLORS.length];
    });
    return map;
  }, [allPoints, points]);
  const [msgModal, setMsgModal] = useState<{ id: string; name: string } | null>(null);
  const [editShiftModal, setEditShiftModal] = useState<typeof shifts[0] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const skipAutoScrollRef = useRef(false);

  const closeEditModal = () => {
    skipAutoScrollRef.current = true;
    setEditShiftModal(null);
    setIsDeleting(false);
  };

  const closeAssignModal = () => {
    skipAutoScrollRef.current = true;
    setAssignModal(null);
  };
  const [msgText, setMsgText] = useState('');
  const [viewAll, setViewAll] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth < 1024);
  const [swapMode, setSwapMode] = useState(false);
  const [swapSelection, setSwapSelection] = useState<typeof shifts[0] | null>(null);
  const [isMagicFilling, setIsMagicFilling] = useState(false);
  const [showPenuryInfo, setShowPenuryInfo] = useState(false);
  const [descriptionModal, setDescriptionModal] = useState<{ name: string; markdown: string } | null>(null);
  
  // @ts-ignore - unused state kept for potential future use
  const [selectedPoint, setSelectedPoint] = useState<any | null>(null);
  // New state for quick shift assignment from grid cell click (manager view)
  const [assignModal, setAssignModal] = useState<{
    teamId: string;
    start: string;
    end: string;
    pointId: string;
    proposedAmount: number;
    proposedCurrency: string;
    notifyAssign: boolean;
  } | null>(null);

  // --- OPTIMIZATION: Lookup maps to avoid .find() in render loops ---
  const teamMap = useMemo(() => new Map(team.map(s => [s.id, s])), [team]);
  const pointsMap = useMemo(() => new Map((allPoints || points).map(p => [p.id, p])), [allPoints, points]);
  const categoriesMap = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);

  const calculateTeamStats = (teamId: string, currentShifts = shifts) => {
    const teamShifts = currentShifts.filter(s => s.teamId === teamId);
    let workMs = 0;
    let otherMs = 0;
    let penury = 0;

    teamShifts.forEach(sh => {
      const pt = pointsMap.get(sh.pointId);
      const cat = pt?.categoryId ? categoriesMap.get(pt.categoryId) : undefined;
      const isGeneral = cat?.isGeneral || false;
      
      const s = new Date(sh.start), e = new Date(sh.end);
      const durationMs = e.getTime() - s.getTime();
      const hours = durationMs / 3600000;

      if (isGeneral) {
        otherMs += durationMs;
      } else {
        workMs += durationMs;
        
        // Penury logic (Work only)
        if (hours >= 8) penury += 1;
        const shour = s.getUTCHours(), ehour = e.getUTCHours();
        if ((shour < 8 && ehour > 4)) penury += 1;
        if ((shour < 15 && ehour > 12)) penury += 1;
        
        const slot = pt?.openingHours.find(h => h.start.substring(0, 16) <= sh.start.substring(0, 16) && h.end.substring(0, 16) >= sh.end.substring(0, 16));
        if (slot?.isHard) penury += 1;
      }
    });

    return { 
      workHours: (workMs / 3600000).toFixed(1), 
      otherHours: (otherMs / 3600000).toFixed(1), 
      penury 
    };
  };
  // Memoized team stats map - calculated once per render instead of N times
  const teamStatsMap = useMemo(() => {
    const map = new Map<string, ReturnType<typeof calculateTeamStats>>();
    team.forEach(member => {
      map.set(member.id, calculateTeamStats(member.id));
    });
    return map;
  }, [team, shifts, allPoints, points, categories]);


  const handleMagicFill = async () => {
    setIsMagicFilling(true);
    try {
      // 1. Identify empty slots
      const slotsToFill: { pointId: string, start: string, end: string, isHard: boolean }[] = [];
      points.forEach(pt => {
        pt.openingHours.forEach(slot => {
          const existingCount = shifts.filter(s => s.pointId === pt.id && s.start === slot.start && s.end === slot.end).length;
          const needed = (slot.requiredTeam || pt.requiredTeam || 1) - existingCount;
          for (let i = 0; i < needed; i++) {
            slotsToFill.push({ pointId: pt.id, start: slot.start, end: slot.end, isHard: !!slot.isHard });
          }
        });
      });

      if (slotsToFill.length === 0) {
        alert("Tous les créneaux sont déjà remplis !");
        return;
      }

      const shuffledSlots = slotsToFill.sort(() => Math.random() - 0.5);
      const newShifts: any[] = [];
      let assignedCount = 0;

      for (const slot of shuffledSlots) {
        const pt = pointsMap.get(slot.pointId);
        if (!pt) continue;

        const eligibleTeam = team.filter(member => {
          // Exclusion check
          if (member.excludeFromMagicFill) return false;

          // Stage check
          if (pt.isStage && !member.isArtist) return false;

          // Category check
          const ptCat = pt.categoryId ? categoriesMap.get(pt.categoryId) : undefined;
          if (!ptCat?.isGeneral && member.categoryId !== pt.categoryId) return false;

          // Restriction check (Skills)
          if (pt.isRestricted && !(member.specialties || []).includes(pt.id)) return false;

          // Overlap check
          const hasOverlap = [...shifts, ...newShifts].some(s => 
            s.teamId === member.id && 
            !(new Date(slot.end) <= new Date(s.start) || new Date(slot.start) >= new Date(s.end))
          );
          if (hasOverlap) return false;

          // Rest check (8h)
          const personalShifts = [...shifts, ...newShifts].filter(s => s.teamId === member.id);
          const lastShift = personalShifts.sort((a, b) => new Date(b.end).getTime() - new Date(a.end).getTime())[0];
          if (lastShift) {
            const rest = (new Date(slot.start).getTime() - new Date(lastShift.end).getTime()) / 3600000;
            if (rest > 0 && rest < 8) return false;
          }
          const nextShift = personalShifts.filter(s => new Date(s.start) >= new Date(slot.end)).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())[0];
          if (nextShift) {
            const restBefore = (new Date(nextShift.start).getTime() - new Date(slot.end).getTime()) / 3600000;
            if (restBefore < 8) return false;
          }

          return true;
        });

        if (eligibleTeam.length > 0) {
          const scored = eligibleTeam.map(s => {
            const stats = calculateTeamStats(s.id, [...shifts, ...newShifts]);
            // Use workHours for equity scoring
            const score = parseFloat(stats.workHours) + (stats.penury * 2);
            return { team: s, score };
          }).sort((a, b) => a.score - b.score);

          const bestScore = scored[0].score;
          const candidates = scored.filter(c => Math.abs(c.score - bestScore) < 0.1);
          const winner = candidates[Math.floor(Math.random() * candidates.length)].team;

          newShifts.push({
            id: crypto.randomUUID(),
            teamId: winner.id,
            pointId: slot.pointId,
            start: slot.start,
            end: slot.end,
            description: "Auto-assigné par Magic Fill"
          });
          assignedCount++;
        }
      }

      if (newShifts.length > 0) {
        for (const sh of newShifts) await addShift(sh);
        alert(`${assignedCount} shifts ont été assignés automatiquement.`);
      } else {
        alert("Aucun team disponible pour ces créneaux.");
      }
    } catch (err) {
      console.error(err);
      alert("Erreur lors de l'auto-assignation.");
    } finally {
      setIsMagicFilling(false);
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isTeamUser = currentUser.role === 'staff' || !!impersonatedTeamId;
  const effectiveTeamId = impersonatedTeamId || currentUser.teamId;
  const currentTeamObj = isTeamUser && effectiveTeamId ? teamMap.get(effectiveTeamId) : null;

  const [viewMode, setViewMode] = useState<'list' | 'gantt'>(() => {
    const saved = localStorage.getItem('wfm_timeline_view_mode');
    if (saved === 'list' || saved === 'gantt') return saved;
    return isTeamUser ? 'list' : 'gantt';
  });

  const handleViewModeChange = (mode: 'list' | 'gantt') => {
    setViewMode(mode);
    localStorage.setItem('wfm_timeline_view_mode', mode);
  };

  const HOUR_PX = isTeamUser ? 96 : (isMobile ? 48 : 72);
  const ROW_H = isTeamUser ? 64 : (isMobile ? 42 : 48);
  const SIDEBAR_W = isTeamUser 
    ? (sidebarCollapsed ? 50 : 120) 
    : (sidebarCollapsed ? (isMobile ? 50 : 60) : (isMobile ? 140 : 210));
  const HEADER_DAY_H = isMobile ? 28 : 36;
  const HEADER_HR_H = isMobile ? 24 : 28;
  const [loadedCount, setLoadedCount] = useState(10);

  const [displayTeam, displayShifts] = useMemo(() => {
    let dTeam = team;
    let dShifts = shifts;

    if (isTeamUser && currentTeamObj) {
      if (!viewAll) {
        dTeam = [currentTeamObj];
        dShifts = shifts.filter(sh => sh.teamId === effectiveTeamId);
      } else {
        if (currentTeamObj.isArtist) {
          // Artists see ALL other artists across ALL scenes + their own shifts
          const allArtistIds = new Set(team.filter(s => s.isArtist).map(s => s.id));
          const stagePointIds = new Set(
            (allPoints || points).filter(pt => pt.isStage).map(pt => pt.id)
          );

          dShifts = shifts.filter(sh => {
            return sh.teamId === effectiveTeamId ||
                   allArtistIds.has(sh.teamId) ||
                   stagePointIds.has(sh.pointId);
          });

          const activeTeamIds = new Set(dShifts.map(sh => sh.teamId));
          dTeam = team.filter(s => s.id === effectiveTeamId || activeTeamIds.has(s.id));
        } else {
          dTeam = team.filter(s => s.categoryId === currentTeamObj.categoryId);
          const displayTeamIds = new Set(dTeam.map(s => s.id));
          dShifts = shifts.filter(s => displayTeamIds.has(s.teamId));

          const myShiftPointIds = new Set(
            shifts
              .filter(s => s.teamId === effectiveTeamId)
              .map(s => s.pointId)
          );

          dShifts = dShifts.filter(sh => {
            if (sh.teamId === effectiveTeamId) return true;
            return myShiftPointIds.has(sh.pointId);
          });

          const activeTeamIds = new Set(dShifts.map(sh => sh.teamId));
          dTeam = dTeam.filter(s => s.id === effectiveTeamId || activeTeamIds.has(s.id));
        }
      }
    }
    // Filter out past shifts for team users (keep point visible, just hide old shifts)
    if (isTeamUser) {
      const now = new Date();
      dShifts = dShifts.filter(sh => new Date(sh.end) > now);
      // Update dTeam to only include members who still have visible shifts (plus self)
      const activeTeamIds = new Set(dShifts.map(sh => sh.teamId));
      if (effectiveTeamId) activeTeamIds.add(effectiveTeamId);
      dTeam = dTeam.filter(s => activeTeamIds.has(s.id));
    }
    return [dTeam, dShifts];
  }, [team, shifts, isTeamUser, currentTeamObj, viewAll, effectiveTeamId, allPoints, points]);

  // Get first available point ID (memoized) for default selection
  const firstPointId = useMemo(() => {
    const pts = allPoints || points;
    return pts.length > 0 ? pts[0].id : '';
  }, [allPoints, points]);

  // Time range calculations (memoized)
  const { minTime, totalW, ticks, dayGroups, showNow, nowL, hasShifts } = useMemo(() => {
    const result = {
      minTime: new Date(),
      totalW: 0,
      ticks: [] as Date[],
      dayGroups: [] as { label: string; ticks: Date[] }[],
      showNow: false,
      nowL: 0,
      hasShifts: false,
    };

    const has = displayShifts.length > 0;
    result.hasShifts = has;
    if (has) {
      const allMs = displayShifts.flatMap((s: any) => [+new Date(s.start), +new Date(s.end)]);
      const minT = new Date(Math.min(...allMs)); minT.setMinutes(0, 0, 0);
      result.minTime = minT;
      const maxT = new Date(Math.max(...allMs)); maxT.setHours(maxT.getHours() + 1, 0, 0, 0);

      result.totalW = Math.round(((maxT.getTime() - minT.getTime()) / 3600000) * HOUR_PX);

      const t = new Date(minT);
      while (t <= maxT) { result.ticks.push(new Date(t)); t.setHours(t.getHours() + 1); }

      result.ticks.forEach((tick: Date) => {
        const key = tick.toLocaleDateString('fr-FR');
        const last = result.dayGroups[result.dayGroups.length - 1];
        if (last && last.label === key) last.ticks.push(tick);
        else result.dayGroups.push({ label: key, ticks: [tick] });
      });

      result.nowL = Math.round((now.getTime() - minT.getTime()) / 3600000 * HOUR_PX);
      result.showNow = now >= minT && now <= maxT;
    }
    return result;
  }, [displayShifts, HOUR_PX]);

  const getL = useCallback((d: Date) => Math.round((d.getTime() - minTime.getTime()) / 3600000 * HOUR_PX), [minTime, HOUR_PX]);
  const getW = useCallback((s: Date, e: Date) => Math.max(Math.round((e.getTime() - s.getTime()) / 3600000 * HOUR_PX) - 2, 4), [HOUR_PX]);

  // Auto-scroll to "now" line or next upcoming shift for optimal mobile experience
  useEffect(() => {
    if (skipAutoScrollRef.current) {
      skipAutoScrollRef.current = false;
      return;
    }
    if (!hasShifts) return;
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      if (showNow) {
        const scrollTarget = nowL - container.clientWidth / 2;
        container.scrollLeft = Math.max(0, scrollTarget);
      } else if (displayShifts.length > 0) {
        const futureShifts = displayShifts
          .filter(s => new Date(s.end) > new Date())
          .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
        const targetShift = futureShifts[0] || displayShifts[0];
        if (targetShift) {
          const shiftStart = new Date(targetShift.start);
          const targetL = getL(shiftStart);
          const scrollTarget = targetL - container.clientWidth / 2;
          container.scrollLeft = Math.max(0, scrollTarget);
        }
      }
    }
  }, [showNow, nowL, totalW, hasShifts]);

  // Reset lazy-load when the team list changes
  useEffect(() => { setLoadedCount(10); }, [displayTeam.length]);

  const visibleTeam = displayTeam.slice(0, loadedCount);
  const hasMore = loadedCount < displayTeam.length;

  const totalH = HEADER_DAY_H + HEADER_HR_H + visibleTeam.length * ROW_H;

  // Group displayShifts by day (chronological) for the vertical list view
  const sortedShifts = [...displayShifts].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  let processedShifts: any[] = sortedShifts;
  if (isTeamUser && viewAll) {
    const groupedShiftsMap = new Map<string, any[]>();
    const nonGrouped: any[] = [];

    sortedShifts.forEach(sh => {
      const pt = pointsMap.get(sh.pointId);
      const cat = pt?.categoryId ? categoriesMap.get(pt.categoryId) : undefined;
      const isArtistCat = cat?.isArtistCategory || 
                         cat?.name.toLowerCase().includes('artist') || 
                         cat?.name.toLowerCase().includes('dj') || 
                         pt?.isStage || 
                         false;
      const isGeneralCat = cat?.isGeneral || !isArtistCat;
      const shouldGroup = pt && isGeneralCat && !pt.isStage;

      if (shouldGroup) {
        // Group key: pointId_start_end
        const key = `${sh.pointId}_${sh.start}_${sh.end}`;
        if (!groupedShiftsMap.has(key)) {
          groupedShiftsMap.set(key, []);
        }
        groupedShiftsMap.get(key)!.push(sh);
      } else {
        nonGrouped.push(sh);
      }
    });

    const finalGrouped: any[] = [];
    groupedShiftsMap.forEach((groupShifts) => {
      // Find if current user has a shift in this group
      const myShift = groupShifts.find(s => s.teamId === effectiveTeamId);
      const rep = myShift || groupShifts[0];
      finalGrouped.push({
        ...rep,
        isGroupedMeal: true,
        groupedCount: groupShifts.length,
        allGroupedTeamIds: groupShifts.map(s => s.teamId)
      });
    });

    processedShifts = [...nonGrouped, ...finalGrouped].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  }

  const shiftGroups: { dateStr: string; shifts: any[] }[] = [];
  processedShifts.forEach(shift => {
    const sDate = new Date(shift.start);
    const dateStr = sDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    const formattedDate = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);

    const lastGroup = shiftGroups[shiftGroups.length - 1];
    if (lastGroup && lastGroup.dateStr === formattedDate) {
      lastGroup.shifts.push(shift);
    } else {
      shiftGroups.push({ dateStr: formattedDate, shifts: [shift] });
    }
  });

  return (
    <div className={`h-full flex flex-col relative overflow-hidden bg-gradient-to-br from-[#0A0B0E] to-[#14161B] ${isTeamUser ? 'pt-12 pb-2 px-3 sm:p-6' : 'p-4 md:p-8'}`}>
      <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none"></div>

      {isTeamUser ? (
        <div className="flex justify-end mb-4 flex-shrink-0 relative z-10">
          <div className="flex flex-wrap items-center gap-2">
            {currentTeamObj && (
              <>
                <button
                  onClick={() => setViewAll(!viewAll)}
                  className="text-xs px-3.5 py-1.5 rounded-full bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 hover:border-indigo-500/50 transition-all font-semibold"
                >
                  {viewAll ? 'Voir mes shifts uniquement' : (currentTeamObj?.isArtist ? 'Voir les autres artistes' : 'Voir toute mon équipe')}
                </button>

                {/* View Mode Toggle Pill Switcher */}
                <div className="flex bg-[#0A0B0E]/80 backdrop-blur-xl p-0.5 rounded-full border border-[#2D3139] shadow-inner">
                  <button
                    onClick={() => handleViewModeChange('list')}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider transition-all duration-300 ${
                      viewMode === 'list' 
                        ? 'text-white bg-gradient-to-r from-indigo-600 to-purple-600 shadow-md shadow-indigo-600/35' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Clock className="w-3 h-3" />
                    Liste
                  </button>
                  <button
                    onClick={() => handleViewModeChange('gantt')}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider transition-all duration-300 ${
                      viewMode === 'gantt' 
                        ? 'text-white bg-gradient-to-r from-indigo-600 to-purple-600 shadow-md shadow-indigo-600/35' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Globe className="w-3 h-3" />
                    Gantt
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-6 flex-shrink-0 gap-4 relative z-10">
          <div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent mb-2">
              Planning Gantt
            </h2>
            <p className="text-sm text-slate-400">
              Cliquez sur un membre pour sa timeline détaillée
            </p>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-2">
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
              <div className="flex gap-2">
                <button 
                  onClick={() => { setSwapMode(!swapMode); setSwapSelection(null); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all ${swapMode ? 'bg-amber-500 text-black border-amber-400 font-bold shadow-lg shadow-amber-500/20' : 'bg-[#1A1D23] border-[#2D3139] hover:border-amber-500/50 hover:text-amber-400'}`}
                >
                  <RefreshCw className={`w-3 h-3 ${swapMode ? 'animate-spin-slow' : ''}`} />
                  {swapMode ? 'Mode Échange Actif' : 'Mode Échange'}
                </button>
                <button
                  onClick={handleMagicFill}
                  disabled={isMagicFilling}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 transition-all font-bold disabled:opacity-50"
                >
                  <Sparkles className="w-3 h-3" />
                  Magic Fill
                </button>
                {onNavigate && (
                  <button
                    onClick={() => onNavigate()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-all font-bold"
                  >
                    <Clock className="w-3 h-3" />
                    Vue Créneaux
                  </button>
                )}
              </div>
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-indigo-500/60 inline-block" /> Planifié</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-emerald-500/70 inline-block" /> Check-in QR ?</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-blue-500/50 inline-block" /> Terminé</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-amber-500/60 inline-block" /> Échange</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-slate-700/60 inline-block" /> Passé</span>
              {showNow && <span className="flex items-center gap-1"><span className="w-0.5 h-3 bg-red-500 inline-block" /> Maintenant</span>}
            </div>
          </div>
        </div>
      )}

      {/* Main View Container */}
      {viewMode === 'list' && isTeamUser ? (
        /* -- Premium Vertical Timeline ------------------------------------------ */
        <div className="flex-1 overflow-y-auto pr-1 space-y-8 z-10 relative custom-scrollbar">
          {!hasShifts ? (
            <div className="h-64 flex flex-col items-center justify-center text-slate-500 bg-[#0A0B0E]/60 border border-[#2D3139]/40 rounded-2xl backdrop-blur-sm">
              <Clock className="w-10 h-10 text-slate-600 mb-3 animate-pulse" />
              <p className="text-sm font-medium">Aucun shift planifié dans votre calendrier.</p>
            </div>
          ) : (
            shiftGroups.map((group) => (
              <div key={group.dateStr} className="space-y-6">
                {/* Day Header */}
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-gradient-to-r from-[#2D3139]/80 to-transparent"></div>
                  <div className="bg-[#14161B] border border-[#2D3139] px-4 py-1.5 rounded-full text-xs font-bold text-slate-300 tracking-wider shadow-md">
                    {group.dateStr}
                  </div>
                  <div className="h-px flex-1 bg-gradient-to-l from-[#2D3139]/80 to-transparent"></div>
                </div>

                {/* List of Shifts for the Day */}
                <div className="relative space-y-6 py-2">
                  {/* Dashed Timeline Line - aligned to centre of connector column */}
                  <div className="absolute left-[4.5rem] sm:left-20 top-2 bottom-2 w-px border-l border-dashed border-[#2D3139]/70 z-0"></div>

                  {group.shifts.map((shift) => {
                    const pt = pointsMap.get(shift.pointId);
                    const sc = pt?.categoryId ? categoriesMap.get(pt.categoryId) : undefined;
                    const member = teamMap.get(shift.teamId);
                    
                    const isArtist = member?.isArtist || false;
                    const sTime = new Date(shift.start);
                    const eTime = new Date(shift.end);
                    const durationHours = ((eTime.getTime() - sTime.getTime()) / 3600000).toFixed(1);

                    const checkedIn = !!shift.actualStart;
                    const checkedOut = !!shift.actualEnd;
                    const past = eTime < new Date();

                    // Missed meal check:
                    // If this shift is a meal shift, does it overlap with any of the user's work shifts?
                    const isMeal = isMealPoint(pt);
                    let isMissedMeal = false;
                    if (isMeal) {
                      const myWorkShifts = shifts.filter(s => {
                        if (s.teamId !== effectiveTeamId) return false;
                        const sPt = pointsMap.get(s.pointId);
                        return !isMealPoint(sPt);
                      });
                      const mStart = sTime.getTime();
                      const mEnd = eTime.getTime();
                      isMissedMeal = myWorkShifts.some(w => {
                        const wStart = new Date(w.start).getTime();
                        const wEnd = new Date(w.end).getTime();
                        return !(mEnd <= wStart || mStart >= wEnd);
                      });
                    }

                    // Status and Badges
                    let statusText = 'Planifié';
                    let statusColorClass = isArtist ? 'text-purple-400 bg-purple-500/10 border-purple-500/20' : 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20';
                    let startDotBg = isArtist ? 'bg-purple-500' : 'bg-indigo-500';
                    let endDotBg = isArtist ? 'bg-amber-400' : 'bg-emerald-400';
                    let lineGradient = isArtist ? 'from-purple-500 to-amber-400' : 'from-indigo-500 to-emerald-400';

                    if (isMissedMeal) {
                      statusText = 'Manqué';
                      statusColorClass = 'text-red-400 bg-red-500/10 border-red-500/20 font-bold';
                      startDotBg = 'bg-red-500';
                      endDotBg = 'bg-red-600';
                      lineGradient = 'from-red-500 to-red-600';
                    } else if (checkedIn && !checkedOut) {
                      statusText = 'En service ?';
                      statusColorClass = 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20 animate-pulse';
                    } else if (checkedIn && checkedOut) {
                      statusText = 'Terminé';
                      statusColorClass = 'text-blue-400 bg-blue-500/10 border-blue-500/20';
                    } else if (shift.swapRequested) {
                      statusText = 'Échange demandé';
                      statusColorClass = 'text-amber-400 bg-amber-500/10 border-amber-500/20';
                    } else if (past) {
                      statusText = 'Passé';
                      statusColorClass = 'text-slate-400 bg-slate-500/10 border-slate-500/20';
                      lineGradient = 'from-slate-600 to-slate-700';
                      startDotBg = 'bg-slate-600';
                      endDotBg = 'bg-slate-700';
                    }

                    // Color Theme classes
                    const cardClass = isMissedMeal
                      ? 'bg-gradient-to-br from-[#2D0F12]/80 via-[#1E0B0D]/90 to-[#120506]/95 hover:from-[#3D1418]/90 hover:to-[#1E0B0D]/100 border-red-500/20 hover:border-red-500/40 shadow-[0_4px_20px_rgba(239,68,68,0.05)] hover:shadow-[0_8px_30px_rgba(239,68,68,0.12)] transition-all duration-300'
                      : (isArtist
                        ? 'bg-gradient-to-br from-[#1E1B4B]/80 via-[#120D26]/90 to-[#0A0714]/95 hover:from-[#1E1B4B]/90 hover:to-[#0A0714]/100 border-purple-500/20 hover:border-amber-500/40 shadow-[0_4px_20px_rgba(139,92,246,0.05)] hover:shadow-[0_8px_30px_rgba(245,158,11,0.12)] transition-all duration-300'
                        : 'bg-gradient-to-br from-[#14161B]/90 via-[#0E1014]/95 to-[#08090C]/98 hover:from-[#14161B]/100 hover:to-[#08090C]/100 border-indigo-500/20 hover:border-emerald-500/40 shadow-[0_4px_20px_rgba(99,102,241,0.03)] hover:shadow-[0_8px_30px_rgba(16,185,129,0.12)] transition-all duration-300');

                    const iconColor = isMissedMeal ? 'text-red-400' : (isArtist ? 'text-amber-400' : 'text-emerald-400');
                    const iconBg = isMissedMeal ? 'bg-red-500/10' : (isArtist ? 'bg-amber-500/10' : 'bg-emerald-500/10');

                    return (
                      <div key={shift.id} className="relative group/item flex items-stretch min-h-[96px]">

                        {/* -- Time column -- always left of the dashed line -- */}
                        <div className="w-14 sm:w-16 flex-shrink-0 flex flex-col justify-between py-3 pr-3 text-right font-mono font-bold select-none">
                          <span className="text-[10px] sm:text-xs text-slate-300 leading-none">{fmtHour(sTime)}</span>
                          <span className="text-[10px] sm:text-xs text-slate-500 leading-none">{fmtHour(eTime)}</span>
                        </div>

                        {/* -- Connector column -- dots + gradient line -- */}
                        <div className="relative w-8 flex-shrink-0 flex flex-col items-center py-3 z-10">
                          {/* Start Dot */}
                          <div className={`w-2.5 h-2.5 rounded-full ${startDotBg} border-2 border-[#0A0B0E] ring-2 ring-indigo-500/25 flex-shrink-0`}></div>
                          {/* Gradient Line */}
                          <div className={`w-0.5 flex-1 bg-gradient-to-b ${lineGradient} my-1`}></div>
                          {/* End Dot */}
                          <div className={`w-2.5 h-2.5 rounded-full ${endDotBg} border-2 border-[#0A0B0E] ring-2 ring-emerald-500/25 flex-shrink-0`}></div>
                        </div>

                        {/* -- Card -- takes remaining width -- */}
                        <div 
                          onClick={() => { if (pt) console.log("shift click", pt?.name); }}
                          className={`cursor-pointer flex-1 my-2 ml-3 border rounded-2xl p-5 transition-all duration-300 flex flex-col md:flex-row md:items-center justify-between gap-4 backdrop-blur-md ${cardClass}`}
                        >
                          <div className="space-y-3 flex-1">
                            {/* Top row: category / name / duration */}
                            <div className="flex flex-wrap items-center gap-2 text-[10px] sm:text-[11px]">
                              {sc?.name && (
                                <span className={`px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                                  isArtist ? 'bg-purple-500/10 text-purple-300 border border-purple-500/20' : 'bg-indigo-500/10 text-indigo-300 border border-indigo-500/20'
                                }`}>
                                  {sc.name}
                                </span>
                              )}
                              {/* Show member name for supervisors or when viewing all */}
                              {(viewAll || currentTeamObj?.isCategorySupervisor) && (shift.isGroupedMeal ? (
                                <span className="px-2 py-0.5 rounded-full font-bold uppercase tracking-wider bg-indigo-500/15 text-indigo-400 border border-indigo-500/20">
                                  {shift.allGroupedTeamIds?.map((tid: string) => teamMap.get(tid)?.name).filter(Boolean).join(', ') || 'Équipe'}
                                </span>
                              ) : member && (
                                <span className={`px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                                  isArtist ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20' : 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
                                }`}>
                                  {member.name}
                                </span>
                              ))}
                              <span className="text-slate-600 font-bold"></span>
                              <span className="text-slate-400 font-semibold uppercase tracking-wider">{durationHours}h de service</span>
                              
                              {(shift.shiftType === 'performance' || shift.shiftType === 'soundcheck') && (
                                <>
                                  <span className="text-slate-600 font-bold"></span>
                                  <span className={`px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                                    shift.shiftType === 'performance' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20' : 'bg-orange-500/15 text-orange-400 border border-orange-500/20'
                                  }`}>
                                    {shift.shiftType === 'performance' ? 'Performance' : 'Soundcheck'}
                                  </span>
                                </>
                              )}
                            </div>

                            {/* Location & Title */}
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-xl mt-0.5 ${iconBg}`}>
                                <PointIcon name={pt?.iconName || 'MapPin'} size={18} className={iconColor} />
                              </div>
                              <div>
                                <h3 className="text-white font-bold text-base tracking-tight">{pt?.name || 'Mission'}</h3>
                                {shift.description && (
                                  <p className="text-slate-300 text-xs mt-1.5 leading-relaxed max-w-2xl font-medium">
                                    {shift.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Right side status & action buttons */}
                          <div className="flex flex-col sm:flex-row md:flex-col items-start sm:items-center md:items-end justify-between md:justify-center gap-3 border-t md:border-t-0 border-[#2D3139]/50 pt-4 md:pt-0">
                            <div className={`px-3 py-1 rounded-full border text-[10px] font-extrabold tracking-widest uppercase ${statusColorClass}`}>
                              {statusText}
                            </div>

                            {/* Point info button — visible when point has a description */}
                            {pt?.descriptionMarkdown && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onNavigate) {
                                    // Open description — handled by parent
                                  }
                                  if (pt) setDescriptionModal({ name: pt.name, markdown: pt.descriptionMarkdown || '' });
                                }}
                                className="flex items-center gap-1.5 text-xs text-amber-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-amber-500/10 border border-transparent hover:border-amber-500/20 transition-all font-semibold"
                                title="Voir les instructions"
                              >
                                <Info className="w-3.5 h-3.5" />
                                Info
                              </button>
                            )}

                            {/* Messaging or action buttons */}
                            {viewAll && !shift.isGroupedMeal && member && member.id !== effectiveTeamId && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMsgModal({ id: member.id, name: member.name });
                                  setMsgText('');
                                }}
                                className="flex items-center gap-1.5 text-xs text-indigo-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-indigo-500/10 border border-transparent hover:border-indigo-500/20 transition-all font-semibold"
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                                Message
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        /* -- Scrollable Gantt chart -- */
        <div 
          ref={scrollContainerRef}
          className="flex-1 overflow-auto rounded-2xl border border-[#2D3139]/50 bg-[#0A0B0E]/80 backdrop-blur-md shadow-2xl relative custom-scrollbar z-10" 
          style={{ position: 'relative' }}
        >
          {!hasShifts ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-500 p-8 min-h-[300px]">
              <Clock className="w-8 h-8 text-slate-600 mb-2 animate-pulse" />
              <p className="text-sm font-medium">Aucun shift planifié.</p>
            </div>
          ) : (
            <div style={{ width: SIDEBAR_W + totalW, minHeight: totalH, position: 'relative' }}>

              {/* -- Sticky header (day + hour rows) --------------------------- */}
              <div style={{ position: 'sticky', top: 0, zIndex: 20, background: 'rgba(19, 21, 26, 0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(45, 49, 57, 0.8)', display: 'flex' }}>
                {/* Corner */}
                <div style={{
                  width: SIDEBAR_W, flexShrink: 0, position: 'sticky', left: 0, zIndex: 25,
                  background: 'rgba(19, 21, 26, 0.95)', backdropFilter: 'blur(12px)',
                  borderRight: '1px solid rgba(45, 49, 57, 0.8)',
                  transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                  <div style={{ height: HEADER_DAY_H, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px' }}>
                    {!sidebarCollapsed && <span style={{ fontSize: 10, color: '#4B5563', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Équipe</span>}
                    <button 
                      onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                      className="p-1 hover:bg-slate-800/50 rounded-md text-slate-500 hover:text-white transition-colors"
                    >
                      {sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                    </button>
                  </div>
                  <div style={{ height: HEADER_HR_H, borderTop: '1px solid #2D3139', display: 'flex', alignItems: 'center', padding: '0 12px' }}>
                    {!sidebarCollapsed && <span style={{ fontSize: 10, color: '#374151' }}>Horaire ?</span>}
                  </div>
                </div>

                {/* Day + hour labels */}
                <div style={{ position: 'relative', width: totalW, flexShrink: 0 }}>
                  {/* Day row */}
                  <div style={{ height: HEADER_DAY_H, position: 'relative' }}>
                    {dayGroups.map((dg, i) => {
                      const left = getL(dg.ticks[0]);
                      const w = dg.ticks.length * HOUR_PX;
                      return (
                        <div key={i} style={{ position: 'absolute', left, width: w, height: '100%', borderRight: '1px solid #2D3139', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 11, color: '#D1D5DB', fontWeight: 600 }}>{fmtDay(dg.ticks[0])}</span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Hour row */}
                  <div style={{ height: HEADER_HR_H, borderTop: '1px solid #2D3139', position: 'relative', display: 'flex' }}>
                    {ticks.map((tick, i) => (
                      <div key={i} style={{ width: HOUR_PX, flexShrink: 0, borderRight: '1px solid #1F2937', display: 'flex', alignItems: 'flex-end', paddingBottom: 3, paddingLeft: 4 }}>
                        <span style={{ fontSize: 9, color: '#4B5563' }}>{fmtHour(tick)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* -- Team rows ------------------------------------------------- */}
              {visibleTeam.map((member, ri) => {
                const rawShifts = displayShifts.filter(s => s.teamId === member.id).sort((a, b) => +new Date(a.start) - +new Date(b.start));
                
                const specificShifts = rawShifts.filter(s => {
                  const p = pointsMap.get(s.pointId);
                  const c = p?.categoryId ? categoriesMap.get(p.categoryId) : undefined;
                  return !c?.isGeneral;
                });

                const processedShifts = rawShifts.flatMap((shift) => {
                  const pt = pointsMap.get(shift.pointId);
                  const shiftCat = pt?.categoryId ? categoriesMap.get(pt.categoryId) : undefined;
                  const isGeneral = shiftCat?.isGeneral === true;

                  if (!isGeneral) {
                    return [{ shift, pt, isGeneral: isGeneral as boolean, adjustedStart: new Date(shift.start), adjustedEnd: new Date(shift.end) }];
                  }

                  let segments = [{ start: new Date(shift.start), end: new Date(shift.end) }];

                  for (const spec of specificShifts) {
                    const spStart = new Date(spec.start);
                    const spEnd = new Date(spec.end);

                    let newSegments: { start: Date, end: Date }[] = [];
                    for (const seg of segments) {
                      if (spEnd <= seg.start || spStart >= seg.end) {
                        newSegments.push(seg);
                      } else if (spStart <= seg.start && spEnd >= seg.end) {
                        // Completely covered
                      } else if (spStart > seg.start && spEnd < seg.end) {
                        newSegments.push({ start: seg.start, end: spStart });
                        newSegments.push({ start: spEnd, end: seg.end });
                      } else if (spStart <= seg.start) {
                        newSegments.push({ start: spEnd, end: seg.end });
                      } else if (spEnd >= seg.end) {
                        newSegments.push({ start: seg.start, end: spStart });
                      }
                    }
                    segments = newSegments;
                  }

                  return segments.filter(seg => seg.end > seg.start).map(seg => ({
                    shift, pt, isGeneral: isGeneral as boolean, adjustedStart: seg.start, adjustedEnd: seg.end
                  }));
                });

                                const top = HEADER_DAY_H + HEADER_HR_H + ri * ROW_H;
                return (
                                    <div 
                    key={member.id} 
                    onContextMenu={(e) => {
                      e.preventDefault();
                      if (!isTeamUser) {
                        const now = new Date();
                        now.setMinutes(0, 0, 0);
                        const startStr = now.toISOString().substring(0, 16);
                        const endStr = new Date(now.getTime() + 3600000).toISOString().substring(0, 16);
                        setAssignModal({
                          teamId: member.id,
                          start: startStr,
                          end: endStr,
                          pointId: firstPointId || '',
                          proposedAmount: 0,
                          proposedCurrency: 'TND',
                          notifyAssign: true,
                        });
                      }
                    }}
                    style={{ position: 'absolute', top, left: 0, right: 0, height: ROW_H, display: 'flex', borderBottom: '1px solid #1F2937' }}
                  >
                    {/* Sidebar label */}
                    <div
                      onClick={() => {
                        if (!isTeamUser) {
                          // For managers: open assign modal with a default 1h slot starting at current hour
                          const now = new Date();
                          now.setMinutes(0, 0, 0);
                          const startStr = now.toISOString().substring(0, 16);
                          const endStr = new Date(now.getTime() + 3600000).toISOString().substring(0, 16);
                          setAssignModal({
                            teamId: member.id,
                            start: startStr,
                            end: endStr,
                            pointId: firstPointId || '',
                            proposedAmount: 0,
                            proposedCurrency: 'TND',
                            notifyAssign: true,
                          });
                        } else {
                          setMsgModal({ id: member.id, name: member.name });
                          setMsgText('');
                        }
                      }}
                      style={{ 
                        width: SIDEBAR_W, flexShrink: 0, position: 'sticky', left: 0, zIndex: 10, 
                        background: '#0D0E12', borderRight: '1px solid #2D3139', 
                        display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'space-between', 
                        padding: sidebarCollapsed ? '0 5px' : '0 10px', cursor: 'pointer', gap: 6,
                        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                      className="hover:bg-[#14161B] transition-colors group/row"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        {member.photo
                          ? <img src={member.photo} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} alt="" />
                          : <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: member.color || '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>{member.name[0]}</div>
                        }
                        {!sidebarCollapsed && (
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <p style={{ fontSize: isMobile ? 10 : 12, fontWeight: 600, color: '#F9FAFB', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.name}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <div className="flex items-center bg-slate-900/50 rounded-md border border-slate-800/50 px-1.5 py-0.5">
                                <span className="text-[8px] sm:text-[9px] text-emerald-400 font-bold uppercase tracking-tight" title="Travail">{teamStatsMap.get(member.id)?.workHours || "0"}h</span>
                                <span className="mx-1 text-[8px] text-slate-600">|</span>
                                <span className="text-[8px] sm:text-[9px] text-slate-500 font-medium" title="Hors-travail">{teamStatsMap.get(member.id)?.otherHours || "0"}h</span>
                              </div>
                              <button 
                                onClick={(e) => { e.stopPropagation(); setShowPenuryInfo(true); }}
                                className="flex items-center gap-0.5 text-[9px] text-amber-500 font-bold hover:text-amber-400 transition-colors"
                              >
                                <Flame className="w-2.5 h-2.5" /> {teamStatsMap.get(member.id)?.penury || 0}
                              </button>
                              {isMobile && (
                                <button
                                  onClick={e => { e.stopPropagation(); setMsgModal({ id: member.id, name: member.name }); setMsgText(''); }}
                                  className="ml-auto text-slate-500 hover:text-indigo-400 p-0.5"
                                >
                                  <MessageSquare className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {!sidebarCollapsed && member.isOnDuty && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981', flexShrink: 0, boxShadow: '0 0 5px #10B981', marginRight: 4 }} />}
                    </div>
                    {!isMobile && !sidebarCollapsed && (
                      <button
                        onClick={e => { e.stopPropagation(); setMsgModal({ id: member.id, name: member.name }); setMsgText(''); }}
                        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#4B5563', background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, display: 'flex', zIndex: 11 }}
                        title="Envoyer un message"
                        className="hover:!text-indigo-400 transition-colors"
                      >
                        <MessageSquare style={{ width: 14, height: 14 }} />
                      </button>
                    )}

                    {/* Bars area */}
                    <div
                      style={{ position: 'relative', flex: 1, cursor: !isTeamUser ? 'crosshair' : 'default' }}
                      className="transition-colors hover:bg-indigo-500/5"
                      onClick={(e) => {
                        if (isTeamUser) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = Math.max(0, e.clientX - rect.left);
                        const clickedTime = new Date(minTime.getTime() + (x / HOUR_PX) * 3600000);
                        clickedTime.setMinutes(clickedTime.getMinutes() < 30 ? 0 : 30, 0, 0);
                        const hourEnd = new Date(clickedTime.getTime() + 3600000);
                        const fmtLocal = (d: Date) => {
                          const y = d.getFullYear();
                          const m = String(d.getMonth() + 1).padStart(2, '0');
                          const day = String(d.getDate()).padStart(2, '0');
                          const h = String(d.getHours()).padStart(2, '0');
                          const min = String(d.getMinutes()).padStart(2, '0');
                          return `${y}-${m}-${day}T${h}:${min}`;
                        };
                        setAssignModal({
                          teamId: member.id,
                          start: fmtLocal(clickedTime),
                          end: fmtLocal(hourEnd),
                          pointId: firstPointId || '',
                          proposedAmount: 0,
                          proposedCurrency: 'TND',
                          notifyAssign: true,
                        });
                      }}
                    >
                      {/* Hour grid lines */}
                      {ticks.map((_, i) => (
                        <div key={i} style={{ position: 'absolute', top: 0, bottom: 0, left: i * HOUR_PX, width: 1, background: '#1F2937', pointerEvents: 'none' }} />
                      ))}

                      {/* Clickable hour cells for manager/admin (empty cells to add shifts) */}
                      {!isTeamUser && ticks.map((tick, i) => {
                        const hourStart = new Date(tick);
                        const hourEnd = new Date(hourStart.getTime() + 3600000);
                        // Check if this hour slot has any shift already rendered
                        const hasShift = processedShifts.some(({ adjustedStart, adjustedEnd }) => {
                          const s = adjustedStart, e = adjustedEnd;
                          return !(hourEnd <= s || hourStart >= e);
                        });
                        if (hasShift) return null;
                        return (
                          <div
                            key={`cell-${member.id}-${i}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const startStr = hourStart.toISOString().substring(0, 16);
                              const endStr = hourEnd.toISOString().substring(0, 16);
                              setAssignModal({
                                teamId: member.id,
                                start: startStr,
                                end: endStr,
                                pointId: firstPointId || '',
                                proposedAmount: 0,
                                proposedCurrency: 'TND',
                                notifyAssign: true,
                              });
                            }}
                            style={{
                              position: 'absolute',
                              top: 0,
                              bottom: 0,
                              left: i * HOUR_PX,
                              width: HOUR_PX,
                              zIndex: 3,
                              cursor: 'pointer',
                            }}
                            className="hover:bg-indigo-500/10 transition-colors"
                            title={`Cliquer pour ajouter un shift (${fmtHour(hourStart)})`}
                          />
                        );
                      })}
                      {/* Shift bars */}
                      {processedShifts.map(({ shift, pt, isGeneral, adjustedStart, adjustedEnd }, idx) => {
                        const s = adjustedStart, e = adjustedEnd;
                        if (e <= s) return null; // Fully eclipsed by another shift
                        
                        const l = getL(s), w = getW(s, e);
                        const checkedIn = !!shift.actualStart, checkedOut = !!shift.actualEnd;
                        const past = e < now;
                        
                        let bg = '#4F46E5aa', border = '#6366f1'; // Planifié
                        if (pt?.isStage) {
                          const customColor = stageColorsMap[pt.id];
                          if (customColor) {
                            bg = customColor.bg;
                            border = customColor.border;
                          }
                        } else if (shift.shiftType === 'soundcheck') { bg = '#F97316aa'; border = '#F97316'; } // Soundcheck (Orange)
                        else if (shift.shiftType === 'performance') { bg = '#F59E0Baa'; border = '#F59E0B'; } // Performance (Golden/Amber)
                        else if (isGeneral) { bg = '#EAB308aa'; border = '#EAB308'; } // Général (Yellow)
                        if (checkedIn && !checkedOut) { bg = '#059669cc'; border = '#10B981'; }
                        else if (checkedIn && checkedOut) { bg = '#2563EBaa'; border = '#3B82F6'; }
                        else if (shift.swapRequested) { bg = '#D97706aa'; border = '#F59E0B'; }
                        else if (past) { bg = '#37415180'; border = '#4B5563'; }

                        const teamMember = teamMap.get(shift.teamId);
                        const isFreelancerShift = teamMember && teamMember.orgId && teamMember.orgId !== currentOrgId;
                        if (isFreelancerShift) {
                          if (shift.agreementStatus === 'agreed' || shift.agreementStatus === 'approved') {
                            bg = '#6366F120'; border = '#10B981';
                          } else {
                            bg = '#A855F720'; border = '#C084FC';
                          }
                        }
                        
                        
                        const pad = 6;
                        return (
                          <div
                            key={`${shift.id}-${idx}`}
                            title={`${pt?.name} · ${fmtHour(s)} ? ${fmtHour(e)}`}
                            onClick={() => {
                              if (isTeamUser && pt) {
                                console.log("shift click", pt?.name);
                              }
                            }}
                            style={{
                              position: 'absolute',
                              top: pad,
                              bottom: pad,
                              left: l,
                              width: w,
                              background: bg,
                              border: `1px solid ${border}`,
                              borderRadius: isTeamUser ? 10 : 5,
                              display: 'flex',
                              alignItems: 'center',
                              paddingLeft: isTeamUser ? 6 : 5,
                              paddingRight: isTeamUser ? 6 : 5,
                              gap: isTeamUser ? 4 : 3,
                              overflow: 'hidden',
                              fontSize: isTeamUser ? 10 : 9,
                              color: '#fff',
                              fontWeight: 600,
                              letterSpacing: '0.02em',
                              boxShadow: isTeamUser ? '0 3px 8px rgba(0,0,0,0.18)' : 'none',
                            }}
                          >
                            {isTeamUser ? (
                              <div className="flex flex-col justify-center w-full h-full py-1 leading-normal select-none">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <div className="flex-shrink-0 bg-white/15 p-1 rounded-lg">
                                    <PointIcon name={pt?.iconName || 'MapPin'} size={12} className="text-white" />
                                  </div>
                                  {w > 75 ? (
                                    <span className="text-[11px] font-black text-white truncate">{pt?.name}</span>
                                  ) : (
                                    <span className="text-[11px] font-black text-white truncate">{pt?.name.slice(0, 3)}..</span>
                                  )}
                                  {checkedIn && <span className="text-[9px] bg-emerald-500 text-white font-black px-1 rounded flex-shrink-0">?</span>}
                                </div>
                                {w > 90 && (
                                  <div className="text-[9px] text-white/75 font-mono font-bold mt-1 flex items-center gap-1">
                                    <Clock className="w-2.5 h-2.5 opacity-60 flex-shrink-0" />
                                    <span>{fmtHour(s)} - {fmtHour(e)}</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <>
                                 {checkedIn && <span style={{ flexShrink: 0 }}>?</span>}
                                 {isFreelancerShift && (
                                   <span className="flex-shrink-0" title={shift.agreementStatus === 'agreed' || shift.agreementStatus === 'approved' ? 'Prestation Confirmée ?' : 'Prestation en Négociation ??'}>
                                     {shift.agreementStatus === 'agreed' || shift.agreementStatus === 'approved' ? '?' : '??'}
                                   </span>
                                 )}
                                 {w > 55 && (
                                   <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                                     <PointIcon name={pt?.iconName || 'MapPin'} size={11} className="text-white/80 flex-shrink-0" />
                                     <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pt?.name}</span>
                                   </div>
                                 )}
                              </>
                            )}
                            
                            {/* Skill Alert Icon */}
                            {pt?.isRestricted && !teamMap.get(shift.teamId)?.specialties?.includes(pt.id) && (
                              <div 
                                className="ml-auto mr-1 p-0.5 bg-red-600 rounded-full animate-pulse cursor-help group/alert relative z-20"
                                title="Alerte : Team non-spécialisé pour ce point restreint"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  alert(`?? Alerte Compétence : ${teamMap.get(shift.teamId)?.name} n'est pas certifié pour le point "${pt.name}".`);
                                }}
                              >
                                <AlertTriangle className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                            
                            {/* Edit overlay for Admin/Manager */}
                            {!isTeamUser && (
                              <div 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  if (swapMode) {
                                    if (!swapSelection) {
                                      setSwapSelection(shift);
                                    } else if (swapSelection.id === shift.id) {
                                      setSwapSelection(null);
                                    } else {
                                      // Perform swap if duration matches
                                      const d1 = new Date(swapSelection.end).getTime() - new Date(swapSelection.start).getTime();
                                      const d2 = new Date(shift.end).getTime() - new Date(shift.start).getTime();
                                      if (d1 === d2) {
                                        const s1_team = swapSelection.teamId;
                                        const s2_team = shift.teamId;
                                        updateShift(swapSelection.id, { ...swapSelection, teamId: s2_team });
                                        updateShift(shift.id, { ...shift, teamId: s1_team });
                                        setSwapSelection(null);
                                        setSwapMode(false);
                                      } else {
                                        alert("L'échange n'est possible qu'entre shifts de même durée !");
                                      }
                                    }
                                  } else {
                                    setEditShiftModal(shift); 
                                  }
                                }}
                                className={`absolute inset-0 transition-all cursor-pointer z-10 ${swapSelection?.id === shift.id ? 'bg-amber-500/40 border-2 border-amber-400 animate-pulse' : 'bg-white/0 hover:bg-white/10'}`}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {/* Load More button — outside scrollable area, always visible */}
          {hasMore && (
            <div className="flex justify-center py-3 flex-shrink-0 z-10 relative">
              <button
                onClick={() => setLoadedCount(c => c + 10)}
                className="px-4 py-2 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 text-indigo-400 rounded-xl text-xs font-bold transition-all"
              >
                Afficher plus ({displayTeam.length - loadedCount} restants)
              </button>
            </div>
          )}
        </div>
      )}

      {/* Message modal */}
      {msgModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 }}>
          <div className="bg-[#14161B] border border-[#2D3139] p-6 rounded-xl w-full max-w-sm shadow-2xl">
            <h3 className="text-white font-semibold mb-1">Message à {msgModal.name}</h3>
            <p className="text-slate-500 text-xs mb-4">Visible depuis l'app du team.</p>
            <textarea
              value={msgText}
              onChange={e => setMsgText(e.target.value)}
              placeholder="Votre message..."
              autoFocus
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500 mb-4 h-24 resize-none"
            />
            <div className="flex gap-2">
              <button onClick={() => { skipAutoScrollRef.current = true; setMsgModal(null); }} className="flex-1 py-2 bg-[#0A0B0E] hover:bg-[#2D3139] text-slate-300 rounded-lg text-sm transition-colors">Annuler</button>
              <button
                onClick={() => { if (msgText.trim()) { addMessage(msgModal.id, msgText); skipAutoScrollRef.current = true; setMsgModal(null); } }}
                disabled={!msgText.trim()}
                className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-lg text-sm font-medium transition-colors"
              >Envoyer</button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Shift Modal */}
      {editShiftModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#14161B] border border-[#2D3139] w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-[#2D3139] bg-[#1A1D23] flex justify-between items-center">
              <h3 className="text-lg font-bold text-white">Modifier le Shift</h3>
              <button onClick={closeEditModal} className="p-2 hover:bg-[#2D3139] rounded-xl text-slate-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto custom-scrollbar">
              {/* Point Select */}
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Point de mission</label>
                <select 
                  value={editShiftModal.pointId}
                  onChange={e => setEditShiftModal({ ...editShiftModal, pointId: e.target.value })}
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-2.5 text-white text-sm focus:border-indigo-500 focus:outline-none"
                >
                  {(allPoints || points).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>

              {/* Team Select */}
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Membre Team</label>
                <select 
                  value={editShiftModal.teamId}
                  onChange={e => setEditShiftModal({ ...editShiftModal, teamId: e.target.value })}
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-2.5 text-white text-sm focus:border-indigo-500 focus:outline-none"
                >
                  {(() => {
                    const selectedPointObj = pointsMap.get(editShiftModal.pointId);
                    const filteredTeam = selectedPointObj?.isStage 
                      ? team.filter(s => s.isArtist) 
                      : team;
                    return filteredTeam.map(s => <option key={s.id} value={s.id}>{s.name}</option>);
                  })()}
                </select>
              </div>

              {/* Freelancer Tariff & Currency Negotiator */}
              {(() => {
                const selectedTeamObj = teamMap.get(editShiftModal.teamId);
                const isFreelancer = selectedTeamObj && selectedTeamObj.orgId && selectedTeamObj.orgId !== currentOrgId;
                if (!isFreelancer) return null;
                return (
                  <div className="bg-[#1A1D23] p-4 rounded-xl border border-indigo-500/10 space-y-4">
                    <h4 className="text-[10px] uppercase font-black text-indigo-400 tracking-wider flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5" /> Tarification Freelance
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <label className="text-[9px] uppercase font-semibold text-slate-500">Montant Prestation</label>
                        <input 
                          type="number"
                          value={editShiftModal.proposedAmount || 0}
                          onChange={e => setEditShiftModal({ ...editShiftModal, proposedAmount: parseFloat(e.target.value) || 0 })}
                          className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-1.5 text-white text-xs focus:border-indigo-500 focus:outline-none"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[9px] uppercase font-semibold text-slate-500">Devise ({selectedTeamObj.proposedCurrency || 'TND'} ind.)</label>
                        <select 
                          value={editShiftModal.proposedCurrency || 'TND'}
                          onChange={e => setEditShiftModal({ ...editShiftModal, proposedCurrency: e.target.value })}
                          className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-1.5 text-white text-xs focus:border-indigo-500 focus:outline-none"
                        >
                          <option value="TND">TND (????)</option>
                          <option value="EUR">EUR (????)</option>
                          <option value="USD">USD (????)</option>
                        </select>
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-400 flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5 text-indigo-400" />
                      <span>Statut de l'accord : <span className="font-bold text-white uppercase tracking-wider">{editShiftModal.agreementStatus === 'agreed' || editShiftModal.agreementStatus === 'approved' ? 'Confirmé ?' : editShiftModal.agreementStatus === 'negotiated' ? 'Contre-proposition ??' : 'En attente ?'}</span></span>
                    </div>
                  </div>
                );
              })()}

              {/* Times */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Début</label>
                  <input 
                    type="datetime-local"
                    value={editShiftModal.start.substring(0, 16)}
                    onChange={e => setEditShiftModal({ ...editShiftModal, start: e.target.value })}
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-xs focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Fin</label>
                  <input 
                    type="datetime-local"
                    value={editShiftModal.end.substring(0, 16)}
                    onChange={e => setEditShiftModal({ ...editShiftModal, end: e.target.value })}
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-xs focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Danger Zone: Delete Confirmation */}
              {isDeleting ? (
                <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-xl space-y-3">
                  <p className="text-xs text-red-400 font-medium text-center">Êtes-vous sûr de vouloir supprimer ce shift ?</p>
                  <div className="flex gap-2">
                    <button onClick={() => setIsDeleting(false)} className="flex-1 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-bold transition-colors">Annuler</button>
                    <button 
                      onClick={() => { deleteShift(editShiftModal.id); closeEditModal(); }}
                      className="flex-1 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-bold transition-colors"
                    >Supprimer définitivement</button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <button 
                    onClick={() => {
                      const newShift = { ...editShiftModal, id: crypto.randomUUID() };
                      addShift(newShift);
                      closeEditModal();
                    }}
                    className="w-full py-2.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2"
                  >
                    <PartyPopper className="w-3.5 h-3.5" /> Dupliquer ce shift
                  </button>
                  <button 
                    onClick={() => setIsDeleting(true)}
                    className="w-full py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-xl text-xs font-bold transition-all"
                  >
                    Supprimer ce shift
                  </button>
                </div>
              )}
            </div>

            <div className="p-6 bg-[#1A1D23] border-t border-[#2D3139] flex gap-3">
              <button onClick={closeEditModal} className="flex-1 py-3 bg-[#0A0B0E] hover:bg-[#2D3139] text-slate-300 rounded-xl text-sm font-bold transition-colors">Annuler</button>
              <button 
                onClick={() => {
                  const targetPt = pointsMap.get(editShiftModal.pointId);
                  const targetTeam = teamMap.get(editShiftModal.teamId);
                  const isRestricted = targetPt?.isRestricted;
                  const hasSkill = targetTeam?.specialties?.includes(targetPt?.id || '');

                  if (isRestricted && !hasSkill) {
                    if (!confirm(`?? Attention : ${targetTeam?.name} n'est pas spécialisé pour le point restreint "${targetPt?.name}". Voulez-vous forcer l'assignation ?`)) {
                      return;
                    }
                  }
                  
                  const isFreelancerOnSave = targetTeam && targetTeam.orgId && targetTeam.orgId !== currentOrgId;
                  const updatedShift = { ...editShiftModal };
                  if (isFreelancerOnSave) {
                    if (updatedShift.agreementStatus !== 'agreed' && updatedShift.agreementStatus !== 'approved' && updatedShift.agreementStatus !== 'negotiated') {
                      updatedShift.agreementStatus = 'pending_agreement';
                    }
                  } else {
                    updatedShift.agreementStatus = 'approved';
                  }

                  updateShift(editShiftModal.id, updatedShift);
                  closeEditModal();
                }} 
                className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold transition-all shadow-lg shadow-indigo-500/20"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Penury Info Modal */}
      {showPenuryInfo && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-[#14161B] border border-[#2D3139] w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-5 border-b border-[#2D3139] flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <Flame className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h3 className="text-white font-bold">Score de Pénibilité</h3>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Calcul Scientifique</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-300 leading-relaxed">
                Le score de pénibilité aide à équilibrer la fatigue et les conditions de travail entre les membres du team.
              </p>
              <div className="space-y-2.5">
                {[
                  { label: "Horaires Critiques", desc: "Shifts entre 4h-8h ou 12h-15h", pts: "+1" },
                  { label: "Durée Prolongée", desc: "Tout shift de plus de 8 heures", pts: "+1" },
                  { label: "Point Difficile", desc: "Point marqué comme 'Pénible' par l'admin", pts: "+1" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-[#0A0B0E] border border-[#2D3139] rounded-xl">
                    <div>
                      <p className="text-xs font-bold text-white">{item.label}</p>
                      <p className="text-[10px] text-slate-500">{item.desc}</p>
                    </div>
                    <span className="text-xs font-bold text-amber-500">{item.pts}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 bg-[#1A1D23] border-t border-[#2D3139]">
              <button 
                onClick={() => { skipAutoScrollRef.current = true; setShowPenuryInfo(false); }}
                className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-sm transition-all"
              >
                J'ai compris
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Magic Fill Loading Overlay */}
      {isMagicFilling && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[200] flex flex-col items-center justify-center">
          <div className="relative">
            <div className="w-24 h-24 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-indigo-400 animate-pulse" />
            </div>
          </div>
          <h2 className="mt-8 text-2xl font-bold text-white tracking-tight">Magic Fill en cours...</h2>
          <p className="mt-2 text-slate-400 text-sm animate-pulse">
            Optimisation scientifique du planning et répartition équitable...
          </p>
        </div>
      )}
      {/* Point Info Modal - removed for manager/admin view */}

      {/* Description Markdown Modal */}
      {descriptionModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-[#14161B] border border-[#2D3139] w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-[#2D3139] bg-[#1A1D23] flex justify-between items-center">
              <h3 className="text-lg font-bold text-white">Description : {descriptionModal.name}</h3>
              <button 
                onClick={() => { skipAutoScrollRef.current = true; setDescriptionModal(null); }} 
                className="p-2 hover:bg-[#2D3139] rounded-xl text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar prose prose-invert max-w-none text-slate-300 text-sm leading-relaxed">
              <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                {descriptionModal.markdown}
              </Markdown>
            </div>
            <div className="p-4 bg-[#1A1D23] border-t border-[#2D3139] flex justify-end">
              <button 
                onClick={() => { skipAutoScrollRef.current = true; setDescriptionModal(null); }}
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-sm transition-all"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Assign Shift Modal (from grid cell click) */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#14161B] border border-[#2D3139] w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-[#2D3139] bg-[#1A1D23] flex justify-between items-center">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-400" /> Nouveau Shift
              </h3>
              <button onClick={() => closeAssignModal()} className="p-2 hover:bg-[#2D3139] rounded-xl text-slate-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto custom-scrollbar">
              {/* Team Info */}
              <div className="flex items-center gap-3 p-3 bg-[#0A0B0E] rounded-xl border border-[#2D3139]">
                {(() => {
                  const teamMember = teamMap.get(assignModal.teamId);
                  if (!teamMember) return null;
                  return (
                    <>
                      {teamMember.photo
                        ? <img src={teamMember.photo} className="w-10 h-10 rounded-full object-cover flex-shrink-0" alt="" />
                        : <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-bold text-white" style={{ background: teamMember.color || '#6366f1' }}>{teamMember.name[0]}</div>
                      }
                      <div>
                        <p className="text-sm font-bold text-white">{teamMember.name}</p>
                        <p className="text-[10px] text-slate-500">{teamMember.orgId && teamMember.orgId !== currentOrgId ? 'Team Public (Freelance)' : 'Team Interne'}</p>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Point Select */}
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Point de mission</label>
                <select 
                  value={assignModal.pointId}
                  onChange={e => setAssignModal({ ...assignModal, pointId: e.target.value })}
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-2.5 text-white text-sm focus:border-indigo-500 focus:outline-none"
                >
                  {(allPoints || points).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>

              {/* Times */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Début</label>
                  <input 
                    type="datetime-local"
                    value={assignModal.start.substring(0, 16)}
                    onChange={e => setAssignModal({ ...assignModal, start: e.target.value })}
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-xs focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Fin</label>
                  <input 
                    type="datetime-local"
                    value={assignModal.end.substring(0, 16)}
                    onChange={e => setAssignModal({ ...assignModal, end: e.target.value })}
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-xs focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Price section (only for public/freelancer team) */}
              {(() => {
                const teamMember = teamMap.get(assignModal.teamId);
                const isFreelancer = teamMember && teamMember.orgId && teamMember.orgId !== currentOrgId;
                if (!isFreelancer) return null;
                return (
                  <div className="bg-[#1A1D23] p-4 rounded-xl border border-indigo-500/10 space-y-4">
                    <h4 className="text-[10px] uppercase font-black text-indigo-400 tracking-wider flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5" /> Tarification Prestation
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <label className="text-[9px] uppercase font-semibold text-slate-500">Montant proposé</label>
                        <input 
                          type="number"
                          value={assignModal.proposedAmount || 0}
                          onChange={e => setAssignModal({ ...assignModal, proposedAmount: parseFloat(e.target.value) || 0 })}
                          className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-1.5 text-white text-xs focus:border-indigo-500 focus:outline-none"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[9px] uppercase font-semibold text-slate-500">Devise</label>
                        <select 
                          value={assignModal.proposedCurrency || 'TND'}
                          onChange={e => setAssignModal({ ...assignModal, proposedCurrency: e.target.value })}
                          className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-1.5 text-white text-xs focus:border-indigo-500 focus:outline-none"
                        >
                          <option value="TND">TND (Dinars)</option>
                          <option value="EUR">EUR (Euros)</option>
                          <option value="USD">USD (Dollars)</option>
                        </select>
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-400 italic">
                      Le team devra approuver ou négocier le prix avant validation.
                    </p>
                  </div>
                );
              })()}

              {/* Notify Toggle */}
              <div className="flex items-center gap-3 p-4 bg-indigo-500/5 rounded-xl border border-indigo-500/10">
                <input 
                  type="checkbox" 
                  id="assign_notify" 
                  checked={assignModal.notifyAssign}
                  onChange={e => setAssignModal({ ...assignModal, notifyAssign: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-0"
                />
                <label htmlFor="assign_notify" className="text-xs text-slate-300 cursor-pointer select-none">
                  Notifier par email / notification push
                </label>
              </div>
            </div>

            <div className="p-6 bg-[#1A1D23] border-t border-[#2D3139] flex gap-3">
              <button onClick={() => closeAssignModal()} className="flex-1 py-3 bg-[#0A0B0E] hover:bg-[#2D3139] text-slate-300 rounded-xl text-sm font-bold transition-colors">
                Annuler
              </button>
              <button
                onClick={() => {
                  const teamMember = teamMap.get(assignModal.teamId);
                  const isFreelancer = teamMember && teamMember.orgId && teamMember.orgId !== currentOrgId;

                  addShift({
                    teamId: assignModal.teamId,
                    pointId: assignModal.pointId,
                    start: assignModal.start,
                    end: assignModal.end,
                    description: '',
                    proposedAmount: isFreelancer ? assignModal.proposedAmount : undefined,
                    proposedCurrency: isFreelancer ? assignModal.proposedCurrency : undefined,
                    agreementStatus: isFreelancer ? 'pending_agreement' : 'approved',
                  });

                  if (assignModal.notifyAssign) {
                    const pt = pointsMap.get(assignModal.pointId);
                    const startDate = new Date(assignModal.start.endsWith('Z') ? assignModal.start : assignModal.start + 'Z');
                    const endDate = new Date(assignModal.end.endsWith('Z') ? assignModal.end : assignModal.end + 'Z');
                    const dateStr = startDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
                    const startTime = startDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                    const endTime = endDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                    addMessage(
                      assignModal.teamId,
                      `Nouvelle affectation : ${pt?.name || 'Point'} — ${dateStr} de ${startTime} à ${endTime}`,
                      'Admin'
                    );
                  }

                  closeAssignModal();
                }}
                className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold transition-all shadow-lg shadow-indigo-500/20"
              >
                Assigner le Shift
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



















